import axios from 'axios';
import {
  CreateAdminData,
  UserManagement,
  UserFilters,
  ApiResponse,
  PaginatedResponse
} from '../types';
import cookieService from './cookieService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';

const api = axios.create({
  baseURL: `${API_URL}/auth/admin`,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use((config) => {
  const token = cookieService.get('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const createAdminAccount = async (adminData: CreateAdminData): Promise<ApiResponse<UserManagement>> => {
  try {
    const response = await api.post('/create', adminData);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to create admin account' };
  }
};

export const getAllUsers = async (filters: UserFilters = {}): Promise<PaginatedResponse<UserManagement>> => {
  try {
    const response = await api.get('/users', { params: filters });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to fetch users' };
  }
};

export const toggleUserStatus = async (
  userId: string, 
  isActive: boolean
): Promise<ApiResponse<UserManagement>> => {
  try {
    const response = await api.patch(`/users/${userId}/status`, { isActive });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to update user status' };
  }
};

export const updateUserRole = async (
  userId: string, 
  role: string, 
  subRole?: string
): Promise<ApiResponse<UserManagement>> => {
  try {
    const response = await api.patch(`/users/${userId}/role`, { role, subRole });
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to update user role' };
  }
};

export const deleteUser = async (userId: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to delete user' };
  }
};

export const getUserStats = async (): Promise<ApiResponse<{
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  adminUsers: number;
  tenantUsers: number;
  newUsersThisMonth: number;
}>> => {
  try {
    const response = await api.get('/stats');
    return response.data;
  } catch (error: any) {
    throw error.response?.data || { error: 'Failed to fetch user stats' };
  }
};